import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { LinearGradient } from "expo-linear-gradient";
import { Box, Button, Image, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { SafeAreaView } from "react-native";

import { Images, Routes } from "../../constants";

interface SignUpScreenProps {
  navigation: NavigationProp<ParamListBase>;
  brand: { name: string; logo: string };
}

const SignUpScreen: React.FC<SignUpScreenProps> = ({ navigation, brand }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const brandName = brand?.name || "NutriAI";

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <Box flex={1} bg="primary.10">
        {/* Top Container with Image */}
        <Box flex={1} position="relative">
          <Image source={Images.LoginBackground} flex={1} width="100%" resizeMode="cover" alt="Background" />
          <LinearGradient
            colors={["rgba(255, 255, 255, 0)", "rgba(255, 255, 255, 1)"]}
            locations={[0.6, 1]}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </Box>

        {/* Bottom Container with Content */}
        <VStack flex={1} justifyContent="center" alignItems="center" p={4} space={4}>
          {/* Brand Name */}
          <Text
            fontSize="2xl"
            fontWeight="bold"
            textAlign="center"
            fontFamily={theme.fonts.heading}
            color="function.50"
          >
            {brandName}
          </Text>

          {/* Title */}
          <Text fontSize="xl" fontWeight="bold" textAlign="center" fontFamily={theme.fonts.heading} color="function.50">
            {t("onboarding.onboarding_1_sign_up_screen.reach_your_goals")}
          </Text>

          {/* Subtitle */}
          <Text fontSize="md" color="function.30" textAlign="center" fontFamily={theme.fonts.body} mb={4}>
            {t("onboarding.onboarding_1_sign_up_screen.build_healthy_eating_habits")}
          </Text>

          {/* Start Button */}
          <Button
            onPress={() => navigation.navigate(Routes.DIY1GoalScreen)}
            bg="primary.50"
            _pressed={{ bg: "primary.50:alpha.80" }}
            borderRadius={theme.borderRadii.button.pill}
            py={4}
            px={8}
            shadow="10"
            width="80%"
            testID="start-today-button"
          >
            <Text color="primary.10" fontWeight="bold" fontSize="lg" fontFamily={theme.fonts.body}>
              {t("onboarding.onboarding_1_sign_up_screen.start_today")}
            </Text>
          </Button>

          {/* Login Prompt */}
          <Text fontSize="md" color="function.30" textAlign="center" fontFamily={theme.fonts.body} mt={2}>
            {t("onboarding.onboarding_1_sign_up_screen.already_have_account")}
          </Text>

          {/* Login Button */}
          <Button
            onPress={() => navigation.navigate(Routes.LoginScreen, { screen: Routes.LoginScreen })}
            variant="ghost"
            borderRadius={theme.borderRadii.button.pill}
            py={4}
            px={8}
            testID="log-in-button"
          >
            <Text color="primary.50" fontWeight="bold" fontSize="lg" fontFamily={theme.fonts.body}>
              {t("onboarding.onboarding_1_sign_up_screen.log_in")}
            </Text>
          </Button>
        </VStack>
      </Box>
    </SafeAreaView>
  );
};

export default SignUpScreen;
